import React, { useState } from 'react';

interface Option {
  label: string;
  next: string;
}

interface Node {
  type: 'question' | 'conclusion';
  text: string;
  options?: Option[];
  action?: string;
}

interface DiagnosticTreeData {
  startNode: string;
  nodes: Record<string, Node>;
}

interface DiagnosticTreeProps {
  data: DiagnosticTreeData;
}

const DiagnosticTree: React.FC<DiagnosticTreeProps> = ({ data }) => {
  const [currentNodeId, setCurrentNodeId] = useState<string>(data.startNode);
  const [history, setHistory] = useState<string[]>([]);
  const [journeyPath, setJourneyPath] = useState<string[]>([]); // Track actual questions and answers
  const [savingResult, setSavingResult] = useState(false);
  const [feedback, setFeedback] = useState<string>('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const currentNode = data.nodes[currentNodeId];

  const saveResult = async (conclusion: string, path: string[], userFeedback: string) => {
    setSavingResult(true);
    try {
      const email = sessionStorage.getItem('magicToolEmail');
      console.log('Saving result:', { email, conclusion, path, feedback: userFeedback });
      
      const response = await fetch('/api/save-magic-tool-result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          conclusion,
          path,
          feedback: userFeedback,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        console.error('Failed to save:', data);
      } else {
        console.log('Successfully saved result');
        setFeedbackSubmitted(true);
      }
    } catch (error) {
      console.error('Error saving result:', error);
    } finally {
      setSavingResult(false);
    }
  };

  const handleFeedback = (feedbackOption: string) => {
    setFeedback(feedbackOption);
    saveResult(currentNode.text, journeyPath, feedbackOption);
  };

  const handleOptionClick = (nextNodeId: string, optionLabel: string) => {
    const newHistory = [...history, currentNodeId];
    setHistory(newHistory);
    
    // Build human-readable journey path
    const step = `Q: ${currentNode.text} | A: ${optionLabel}`;
    const newJourneyPath = [...journeyPath, step];
    setJourneyPath(newJourneyPath);
    
    setCurrentNodeId(nextNodeId);
  };

  const handleBack = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousNode = newHistory.pop()!;
      setHistory(newHistory);
      setCurrentNodeId(previousNode);
      
      // Remove last step from journey path
      const newJourneyPath = [...journeyPath];
      newJourneyPath.pop();
      setJourneyPath(newJourneyPath);
    }
  };

  const handleRestart = () => {
    setCurrentNodeId(data.startNode);
    setHistory([]);
    setJourneyPath([]);
    setFeedback('');
    setFeedbackSubmitted(false);
  };

  if (!currentNode) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <p className="text-red-600">Error: Node not found</p>
        <button
          onClick={handleRestart}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Restart
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Progress indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>Question {history.length + 1}</span>
          {history.length > 0 && (
            <button
              onClick={handleBack}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              ← Back
            </button>
          )}
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
            style={{ width: `${Math.min((history.length + 1) * 10, 100)}%` }}
          />
        </div>
      </div>

      {/* Question or Conclusion */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
        {currentNode.type === 'question' ? (
          <>
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
                Question
              </span>
              <h2 className="text-2xl font-bold text-gray-900">
                {currentNode.text}
              </h2>
            </div>

            <div className="space-y-3">
              {currentNode.options?.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(option.next, option.label)}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg text-gray-900 group-hover:text-blue-700">
                      {option.label}
                    </span>
                    <span className="text-gray-400 group-hover:text-blue-500">
                      →
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full mb-4">
                ✓ Solution
              </span>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {currentNode.text}
              </h2>
            </div>

            {currentNode.action && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Action Steps:</h3>
                <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                  {currentNode.action}
                </div>
              </div>
            )}

            {/* Feedback Section */}
            {!feedbackSubmitted ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">💬 Was this helpful?</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleFeedback('Very Helpful')}
                    disabled={savingResult}
                    className="px-4 py-3 bg-green-100 text-green-800 font-medium rounded-lg hover:bg-green-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <span>😊</span>
                    <span>Very Helpful</span>
                  </button>
                  <button
                    onClick={() => handleFeedback('Helpful')}
                    disabled={savingResult}
                    className="px-4 py-3 bg-blue-100 text-blue-800 font-medium rounded-lg hover:bg-blue-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <span>🙂</span>
                    <span>Helpful</span>
                  </button>
                  <button
                    onClick={() => handleFeedback('Not Very Helpful')}
                    disabled={savingResult}
                    className="px-4 py-3 bg-orange-100 text-orange-800 font-medium rounded-lg hover:bg-orange-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <span>😐</span>
                    <span>Not Very Helpful</span>
                  </button>
                  <button
                    onClick={() => handleFeedback('Not Helpful')}
                    disabled={savingResult}
                    className="px-4 py-3 bg-red-100 text-red-800 font-medium rounded-lg hover:bg-red-200 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <span>😞</span>
                    <span>Not Helpful</span>
                  </button>
                </div>
                {savingResult && (
                  <p className="text-sm text-gray-600 mt-3 text-center">Saving your feedback...</p>
                )}
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-center">
                <p className="text-green-800 font-medium">✓ Thank you for your feedback!</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleRestart}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Start Over
              </button>
              {history.length > 0 && (
                <button
                  onClick={handleBack}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
                >
                  ← Back
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Restart button for questions */}
      {currentNode.type === 'question' && history.length > 0 && (
        <div className="text-center">
          <button
            onClick={handleRestart}
            className="text-gray-600 hover:text-gray-900 text-sm underline"
          >
            Start Over
          </button>
        </div>
      )}
    </div>
  );
};

export default DiagnosticTree;
